import { AnyAction } from 'redux';
import { Injectable } from '@angular/core';
import {
  Produktlinie,
  TierartEnum,
  TkvVorbelegungdaten,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import { GlobalAction, TARIF_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { Heilbehandlungsschutz, Selbstbeteiligung } from '@ps20/bff-models';
import {
  BehandlungEnum,
  GeschlechtEnum,
  KatzenhaltungEnum,
} from './reducers/versicherungsnehmer.reducer';
import { AntragsummerGenerated } from './actiontypes/antragsummerGenerated';

export interface TkvAction<T = undefined> extends AnyAction {
  payload?: T;
}

@Injectable()
export class TkvActions {
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'TKV_ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'TKV_ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly PLZ_CHANGED = 'TKV_PLZ_CHANGED';
  static readonly ORT_CHANGED = 'TKV_ORT_CHANGED';
  static readonly STRASSE_CHANGED = 'TKV_STRASSE_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'TKV_HAUSNUMMER_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'TKV_GEBURTSDATUM_CHANGED';
  static readonly GESCHLECHT_CHANGED = 'TKV_GESCHLECHT_CHANGED';
  static readonly RASSE_CHANGED = 'TKV_RASSE_CHANGED';
  static readonly GEBURTSDATUM_TIER_CHANGED = 'TKV_GEBURTSDATUM_TIER_CHANGED';
  static readonly TIER_KASTRIERT_STERILISIERT_CHANGED = 'TKV_TIER_KASTRIERT_STERILISIERT_CHANGED';
  static readonly IN_BEHANDLUNG_CHANGED = 'TKV_IN_BEHANDLUNG_CHANGED';
  static readonly BEHANDLUNGEN_CHANGED = 'TKV_BEHANDLUNGEN_CHANGED';
  static readonly ANZAHL_OPERATIONEN_CHANGED = 'TKV_ANZAHL_OPERATIONEN_CHANGED';
  static readonly ERKRANKUNGEN_CHANGED = 'TKV_ERKRANKUNGEN_CHANGED';
  static readonly KATZENHALTUNG_CHANGED = 'TKV_KATZENHALTUNG_CHANGED';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'TKV_SELBSTBETEILIGUNG_CHANGED';
  static readonly PRODUKTLINIEN_CHANGED = 'TKV_PRODUKTLINIEN_CHANGED';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly ZUSATZOPTION_CHECKED = 'ZUSATZOPTION_CHECKED';
  static readonly ZUSATZOPTION_UNCHECKED = 'ZUSATZOPTION_UNCHECKED';
  static readonly HEILBEHANDLUNGSSCHUTZ_CHANGED = 'TKV_HEILBEHANDLUNGSSCHUTZ_CHANGED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'TKV_ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly TKV_TIERART_CHANGED = 'TKV_TIERART_CHANGED';
  static readonly QUOTEDB_REQUESTED_TKV = 'QUOTEDB_REQUESTED_TKV';
  static readonly VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED = 'VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED';
  static readonly PRODUKT_ZUSATZOPTIONEN_VORBELEGT = 'PRODUKT_ZUSATZOPTIONEN_VORBELEGT';
  static readonly HANDLE_TKV_NAVIGATION_TO_ANGEBOT = 'HANDLE_TKV_NAVIGATION_TO_ANGEBOT';

  produktZusatzOptionenVorbelegt(
    vorbelegundaten: TkvVorbelegungdaten,
  ): TkvAction<TkvVorbelegungdaten> {
    return {
      type: TkvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT,
      payload: vorbelegundaten,
    };
  }

  tkvTierartChanged(tierart: TierartEnum): TkvAction<TierartEnum> {
    return { type: TkvActions.TKV_TIERART_CHANGED, payload: tierart };
  }

  absprungInsDashboard(): TkvAction {
    return { type: TkvActions.ABSPRUNG_INS_DASHBOARD };
  }

  vorschlagdatenChanged(): TkvAction {
    return { type: TkvActions.VORSCHLAGDATEN_CHANGED };
  }

  zurueckZumShopModalOpen(): TkvAction {
    return { type: TkvActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): TkvAction {
    return { type: TkvActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  plzChanged(changedPlz: string): TkvAction<string> {
    return { type: TkvActions.PLZ_CHANGED, payload: changedPlz };
  }

  ortChanged(changedOrt: string): TkvAction<string> {
    return { type: TkvActions.ORT_CHANGED, payload: changedOrt };
  }

  strasseChanged(changedStrasse: string): TkvAction<string> {
    return { type: TkvActions.STRASSE_CHANGED, payload: changedStrasse };
  }

  hausnummerChanged(changedHausnummer: string): TkvAction<string> {
    return { type: TkvActions.HAUSNUMMER_CHANGED, payload: changedHausnummer };
  }

  geburtsdatumChanged(geburtsdatum: string): TkvAction<string> {
    return { type: TkvActions.GEBURTSDATUM_CHANGED, payload: geburtsdatum };
  }

  geschlechtChanged(geschlecht: GeschlechtEnum): TkvAction<GeschlechtEnum> {
    return { type: TkvActions.GESCHLECHT_CHANGED, payload: geschlecht };
  }

  tierrasseChanged(rasse: ValueLabelElement): TkvAction<ValueLabelElement> {
    return { type: TkvActions.RASSE_CHANGED, payload: rasse };
  }

  geburtsdatumTierChanged(geburtsdatumTier: string): TkvAction<string> {
    return {
      type: TkvActions.GEBURTSDATUM_TIER_CHANGED,
      payload: geburtsdatumTier,
    };
  }

  tierKastriertChanged(isTierKastriertOderSterilisiert: boolean): TkvAction<boolean> {
    return {
      type: TkvActions.TIER_KASTRIERT_STERILISIERT_CHANGED,
      payload: isTierKastriertOderSterilisiert,
    };
  }

  inBehandlungChanged(isBehandelt: boolean): TkvAction<boolean> {
    return { type: TkvActions.IN_BEHANDLUNG_CHANGED, payload: isBehandelt };
  }

  behandlungenChanged(changedBehandlung: BehandlungEnum[]): TkvAction<BehandlungEnum[]> {
    return {
      type: TkvActions.BEHANDLUNGEN_CHANGED,
      payload: changedBehandlung,
    };
  }

  anzahlOperationenChanged(operationen: number): TkvAction<number> {
    return {
      type: TkvActions.ANZAHL_OPERATIONEN_CHANGED,
      payload: operationen,
    };
  }

  erkrankungenChanged(hasErkrankungen: boolean): TkvAction<boolean> {
    return { type: TkvActions.ERKRANKUNGEN_CHANGED, payload: hasErkrankungen };
  }

  katzenhaltungChanged(katzenhaltung: KatzenhaltungEnum): TkvAction<KatzenhaltungEnum> {
    return { type: TkvActions.KATZENHALTUNG_CHANGED, payload: katzenhaltung };
  }

  selbstbeteiligungChanged(selbstbeteiligung: Selbstbeteiligung): TkvAction<Selbstbeteiligung> {
    return {
      type: TkvActions.SELBSTBETEILIGUNG_CHANGED,
      payload: selbstbeteiligung,
    };
  }

  tarifChecked(index: number): TkvAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): TkvAction<Produktlinie[]> {
    return { type: TkvActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  antragsnummerGenerated(antragsnummer: string): TkvAction<AntragsummerGenerated> {
    return {
      type: TkvActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer },
    };
  }

  preiseRequested(): TkvAction {
    return { type: TkvActions.PREISE_REQUESTED };
  }

  zusatzoptionChecked(name: string): TkvAction<ZusatzoptionCheckedPayload> {
    return { type: TkvActions.ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): TkvAction<ZusatzoptionCheckedPayload> {
    return { type: TkvActions.ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  heilbehandlungsschutzChanged(
    schutzsumme: Heilbehandlungsschutz,
  ): TkvAction<Heilbehandlungsschutz> {
    return {
      type: TkvActions.HEILBEHANDLUNGSSCHUTZ_CHANGED,
      payload: schutzsumme,
    };
  }

  zumAngebotButtonClicked(): TkvAction {
    return { type: TkvActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  quoteDBRequested(): TkvAction {
    return { type: TkvActions.QUOTEDB_REQUESTED_TKV };
  }

  vorbelegungForTkvRequested(
    vorbelegungdaten: TkvVorbelegungdaten,
  ): GlobalAction<TkvVorbelegungdaten> {
    return {
      type: TkvActions.VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED,
      payload: vorbelegungdaten,
    };
  }

  handleTkvNavigationToAngebot(): TkvAction {
    return { type: TkvActions.HANDLE_TKV_NAVIGATION_TO_ANGEBOT };
  }
}

export interface ZusatzoptionCheckedPayload {
  name: string;
}
